import { useAction, useAtom } from '@reatom/npm-react'
import { LayoutGroup, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { PremiumText } from '@root/entities/premium/ui'
import {
  HeadsetIcon,
  NotificationBellIcon,
  PremiumGradientIcon,
  WalletTrackerAlertIcon,
} from '@root/shared/ui-kit'

import {
  notificationCenterPersonalWidgetsOrderAtom,
  notificationCenterPersonalWidgetsVariantsAtom,
  notificationCenterSharedWidgetsOrderAtom,
  setNotificationCenterPersonalWidgetsRowsAction,
} from '../../atoms'
import { PAIRED_ROW_SIZE, toWidgetRows } from '../../model'

import { NotificationCenterSettingsLayout } from './notification-center-settings-layout'
import {
  NotificationCenterSettingsWidget,
  WIDGET_SIZE,
} from './notification-center-settings-widget'
import { useWidgetRowsDrag } from './use-widget-rows-drag'

import type {
  NotificationCenterSharedWidget,
  NotificationWidget,
} from '@root/widgets/notification-widgets/model'

const ICON_SIZE = 20

export const NotificationCenterSettingsBody: React.FC = () => {
  const { t } = useTranslation()
  const [personalOrder] = useAtom(notificationCenterPersonalWidgetsOrderAtom)
  const [personalVariants] = useAtom(
    notificationCenterPersonalWidgetsVariantsAtom,
  )
  const setPersonalRows = useAction(
    setNotificationCenterPersonalWidgetsRowsAction,
  )
  const [sharedOrder, setSharedOrder] = useAtom(
    notificationCenterSharedWidgetsOrderAtom,
  )

  const personalRows = toWidgetRows(personalOrder, personalVariants)
  const personalDrag = useWidgetRowsDrag({
    rows: personalRows,
    pairing: true,
    onChange: setPersonalRows,
  })

  const sharedRows = toWidgetRows(sharedOrder)
  const sharedDrag = useWidgetRowsDrag({
    rows: sharedRows,
    onChange: (rows) => setSharedOrder(rows.flat()),
  })

  const pairedCount =
    personalRows[0]?.length === PAIRED_ROW_SIZE ? PAIRED_ROW_SIZE : 0

  const personalWidgets: Record<
    NotificationWidget,
    { title: React.ReactNode }
  > = {
    profile: { title: t('common.profile') },
    wallets: { title: t('common.wallets') },
    txs: { title: t('notification-center.last_transactions') },
  }

  const sharedWidgets: Record<
    NotificationCenterSharedWidget,
    { title: React.ReactNode; icon: React.ReactNode }
  > = {
    walletTracker: {
      title: <WalletTrackerTitle>{t('wallet-tracker')}</WalletTrackerTitle>,
      icon: <WalletTrackerAlertIcon size={ICON_SIZE} color="var(--amber-9)" />,
    },
    premium: {
      title: <PremiumText>{t('common.premium')}</PremiumText>,
      icon: <PremiumGradientIcon size={ICON_SIZE} />,
    },
    notifications: {
      title: t('notification-center.notifications'),
      icon: <NotificationBellIcon size={ICON_SIZE} />,
    },
    support: {
      title: t('support'),
      icon: <HeadsetIcon size={ICON_SIZE} />,
    },
  }

  return (
    <Root>
      <Heading>{t('notification-center.personal_widgets')}</Heading>
      <LayoutGroup id="notification-center-personal-widgets">
        <List
          ref={personalDrag.groupRef}
          data-dragging={personalDrag.dragging || undefined}
        >
          {personalOrder.map((widget, index) => {
            const compact = index < pairedCount
            return (
              <Slot
                key={widget}
                layout={!personalDrag.dragging}
                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                data-compact={compact || undefined}
              >
                <NotificationCenterSettingsWidget
                  value={widget}
                  title={personalWidgets[widget].title}
                  compact={compact}
                  withRefresh
                  {...personalDrag.itemProps(widget)}
                />
              </Slot>
            )
          })}
        </List>
      </LayoutGroup>
      <Heading>{t('notification-center.personal_widgets')}</Heading>
      <NotificationCenterSettingsLayout />
      <LayoutGroup id="notification-center-shared-widgets">
        <List
          ref={sharedDrag.groupRef}
          data-dragging={sharedDrag.dragging || undefined}
        >
          {sharedOrder.map((widget) => (
            <Slot
              key={widget}
              layout={!sharedDrag.dragging}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            >
              <NotificationCenterSettingsWidget
                value={widget}
                title={sharedWidgets[widget].title}
                icon={sharedWidgets[widget].icon}
                {...sharedDrag.itemProps(widget)}
              />
            </Slot>
          ))}
        </List>
      </LayoutGroup>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 10px;
`

const Heading = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'h2',
})`
  color: var(--text-11);

  padding-block: 6px 0px;
`

const WalletTrackerTitle = styled.span`
  color: var(--amber-9);
`

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  position: relative;
  /* Room for the drop outlines, which the scroll container would clip. */
  padding: 2px;
`

const Slot = styled(motion.div)`
  box-sizing: border-box;
  border-radius: 8px;
  height: ${WIDGET_SIZE}px;
  min-width: 0;
  grid-column: 1 / -1;
  outline: 1px dashed transparent;
  outline-offset: 1px;
  transition:
    outline-color 0.2s ease-in-out,
    background-color 0.2s ease-in-out;

  &[data-compact] {
    grid-column: auto;
  }

  ${List}[data-dragging] & {
    outline-color: var(--backgroundA-5);
    background-color: var(--backgroundA-1);
  }
`
